/**
 * Public Landing embed — StatementFlow-style interactive case map.
 * Uses the Fusion synthetic seed (client-only). "Open in Fusion Canvas" enters
 * the guest /demo/fusion sandbox the same way Workbench uses /demo/workbench.
 * Isolated from FusionCanvas globals (does not touch window.__fusionCy or the fusion UI store).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CytoscapeComponent from 'react-cytoscapejs';
import type cytoscape from 'cytoscape';
import type { ElementDefinition } from 'cytoscape';
import { clsx } from 'clsx';
import { Maximize2, Minus, Plus, ExternalLink, Waypoints } from 'lucide-react';
import { fusionStylesheet, type FusionModule } from '../types/fusion';
import { toCyElements } from '../data/fusionSeed';
import { fitTo, runNamedLayout, type LayoutPreset } from './fusion/fusionLayout';
import { CanvasGridBackground } from './workbench/CanvasGridBackground';
import { enterGuestDemo, fusionHrefForDemo } from '../utils/guestDemo';
import { hasActiveSession } from '../utils/session';

const STYLE = fusionStylesheet();
const LANDING_FUSION_DEMO = 'fusion-ingestion-replay';

const MODULE_CHIPS: { id: 'all' | FusionModule; label: string }[] = [
    { id: 'all', label: 'All surfaces' },
    { id: 'bank', label: 'Bank' },
    { id: 'crypto', label: 'Crypto' },
    { id: 'social', label: 'Social' },
    { id: 'company', label: 'Company' },
    { id: 'shared', label: 'Identity' },
];

/** Full seed with every ingest step visible (landing shows the complete case at once). */
function landingElements(): ElementDefinition[] {
    return toCyElements().map((el) => ({
        ...el,
        data: { ...(el.data as Record<string, unknown>), step: 1 },
    }));
}

function applyModuleFilter(cy: cytoscape.Core, module: 'all' | FusionModule) {
    cy.batch(() => {
        cy.elements().removeClass('cs-modhidden');
        if (module === 'all') return;
        cy.nodes().forEach((n) => {
            const m = String(n.data('module') || '');
            if (m !== module && m !== 'shared') {
                n.addClass('cs-modhidden');
                n.connectedEdges().addClass('cs-modhidden');
            }
        });
        cy.nodes('[module="shared"]').forEach((hub) => {
            const visible = hub.connectedEdges().filter((e) => !e.hasClass('cs-modhidden'));
            if (visible.length === 0 && module !== 'shared') {
                hub.addClass('cs-modhidden');
            }
        });
    });
}

export function LandingCaseMap() {
    const navigate = useNavigate();
    const cyRef = useRef<cytoscape.Core | null>(null);
    const elements = useMemo(() => landingElements(), []);
    const [module, setModule] = useState<'all' | FusionModule>('all');
    const [layout, setLayout] = useState<LayoutPreset>('flow');
    const [selected, setSelected] = useState<{ id: string; label: string; module?: string; type?: string } | null>(null);

    const onCy = useCallback((cy: cytoscape.Core) => {
        cyRef.current = cy;
        cy.minZoom(0.15);
        cy.maxZoom(2.5);
        runNamedLayout(cy, 'flow');
        requestAnimationFrame(() => fitTo(cy, undefined, 40, 0));

        cy.on('tap', 'node', (evt) => {
            const n = evt.target;
            setSelected({
                id: String(n.id()),
                label: String(n.data('label') || n.id()),
                module: n.data('module') ? String(n.data('module')) : undefined,
                type: n.data('type') ? String(n.data('type')) : undefined,
            });
            cy.elements().unselect();
            n.select();
            n.connectedEdges().select();
        });
        cy.on('tap', (evt) => {
            if (evt.target === cy) {
                setSelected(null);
                cy.elements().unselect();
            }
        });
    }, []);

    useEffect(() => {
        const cy = cyRef.current;
        if (!cy) return;
        applyModuleFilter(cy, module);
        fitTo(cy, undefined, 40, 280);
    }, [module]);

    useEffect(() => {
        const cy = cyRef.current;
        if (!cy) return;
        runNamedLayout(cy, layout);
        requestAnimationFrame(() => fitTo(cy, undefined, 40, 280));
    }, [layout]);

    const zoomBy = (factor: number) => {
        const cy = cyRef.current;
        if (!cy) return;
        cy.zoom({ level: cy.zoom() * factor, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } });
    };

    const openFullDemo = () => {
        const loggedIn = hasActiveSession();
        if (!loggedIn) enterGuestDemo();
        navigate(fusionHrefForDemo(LANDING_FUSION_DEMO, { guest: !loggedIn }));
    };

    return (
        <section
            id="case-demo"
            data-testid="landing-case-map"
            className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 scroll-mt-8"
        >
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3 text-left">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-1">
                        Fusion Canvas demo
                    </p>
                    <h2 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">
                        See how a case ties together
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Multi-surface follow-on: bank statements, USDT off-ramp, exchange deposit, and OSINT — one identity hub. Drag, zoom, click a node.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={openFullDemo}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-brand-gold-hover transition-colors"
                    data-testid="landing-open-fusion-demo"
                >
                    Open in Fusion Canvas
                    <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                </button>
            </div>

            <div className="rounded-2xl border border-border/80 bg-card shadow-sm overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-border/70 bg-card/50">
                    <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <Waypoints className="w-3.5 h-3.5 text-primary" />
                        <span>Money flow · R. Kumar</span>
                        <span className="hidden sm:inline text-border">·</span>
                        <span className="hidden sm:inline font-medium normal-case tracking-normal">
                            Synthetic training case
                        </span>
                    </div>
                    <div className="flex items-center gap-1 p-0.5 rounded-md bg-background border border-border/60">
                        {([
                            { id: 'flow' as const, label: 'Flow chart' },
                            { id: 'organic' as const, label: 'Case map' },
                        ]).map((opt) => (
                            <button
                                key={opt.id}
                                type="button"
                                onClick={() => setLayout(opt.id)}
                                className={clsx(
                                    'px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors',
                                    layout === opt.id
                                        ? 'bg-primary text-primary-foreground'
                                        : 'text-muted-foreground hover:text-foreground',
                                )}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex flex-wrap gap-1.5 px-3 py-2 border-b border-border/50">
                    {MODULE_CHIPS.map((chip) => (
                        <button
                            key={chip.id}
                            type="button"
                            onClick={() => setModule(chip.id)}
                            className={clsx(
                                'px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors',
                                module === chip.id
                                    ? 'bg-primary/10 border-primary/40 text-primary'
                                    : 'bg-card border-border/60 text-muted-foreground hover:text-foreground',
                            )}
                        >
                            {chip.label}
                        </button>
                    ))}
                </div>

                <div className="relative h-[min(560px,72vh)] bg-canvas">
                    <CanvasGridBackground />
                    <CytoscapeComponent
                        elements={elements as any}
                        stylesheet={STYLE as any}
                        style={{ width: '100%', height: '100%', position: 'relative', zIndex: 1 }}
                        cy={onCy}
                    />

                    <div className="absolute bottom-3 left-3 z-[2] flex flex-col gap-1">
                        <button
                            type="button"
                            aria-label="Zoom in"
                            onClick={() => zoomBy(1.25)}
                            className="w-8 h-8 rounded-md border border-border bg-card/95 shadow-xs flex items-center justify-center hover:bg-muted"
                        >
                            <Plus className="w-3.5 h-3.5" />
                        </button>
                        <button
                            type="button"
                            aria-label="Zoom out"
                            onClick={() => zoomBy(0.8)}
                            className="w-8 h-8 rounded-md border border-border bg-card/95 shadow-xs flex items-center justify-center hover:bg-muted"
                        >
                            <Minus className="w-3.5 h-3.5" />
                        </button>
                        <button
                            type="button"
                            aria-label="Fit view"
                            onClick={() => cyRef.current && fitTo(cyRef.current, undefined, 40, 280)}
                            className="w-8 h-8 rounded-md border border-border bg-card/95 shadow-xs flex items-center justify-center hover:bg-muted"
                        >
                            <Maximize2 className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    <p className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[2] text-[10px] text-muted-foreground bg-card/80 border border-border/50 rounded-md px-3 py-1 pointer-events-none hidden sm:block">
                        Pan · zoom · click a node — then open full Fusion for Inspector &amp; timeline
                    </p>
                </div>

                {selected && (
                    <div className="px-3 py-2.5 border-t border-border/70 bg-muted/20 text-left flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="text-xs font-semibold text-foreground">{selected.label}</span>
                        {selected.type && (
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                                {selected.type.replace(/_/g, ' ')}
                            </span>
                        )}
                        {selected.module && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-primary/10 text-primary font-medium">
                                {selected.module}
                            </span>
                        )}
                        <span className="text-[10px] font-mono text-muted-foreground ml-auto truncate max-w-[40%]">
                            {selected.id}
                        </span>
                    </div>
                )}
            </div>

            <p className="mt-2 text-[11px] text-muted-foreground text-center">
                Illustrative Fusion mock — not live chain data. Open in Fusion Canvas for timeline replay, Inspector, and Build / Journal tabs.
            </p>
        </section>
    );
}
